import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  notification,
  Typography,
  Avatar,
  Row,
  Col,
  Divider,
  Spin,
  Space,
  Tag,
  Tooltip
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EditOutlined,
  ClearOutlined,
  BulbOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { httpService } from '../../services/httpService';
import { useTheme } from '../../theme/ThemeProvider';
import { UserProfile } from '../../types';
import { clearShahsaaCache } from '../../utils/cacheUtils';
import { extractUserInfoFromToken } from '../../utils/tokenUtils';
import './styles.css';

const { Title, Text } = Typography;

const Profile: React.FC = () => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);
  const navigate = useNavigate();
  const { theme, mode, toggleTheme } = useTheme();

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      
      // Try to get profile from API first
      try {
        const profile = await httpService.getUserProfile();
        setUserProfile(profile.data || profile);
      } catch (apiError) {
        console.log('API profile fetch failed, using local data');
      }
      
      // Extract user info from JWT token and localStorage
      const token = httpService.getToken();
      const localUserInfo = httpService.getUserInfo();
      
      if (token) {
        // Extract username from JWT token
        const tokenUserInfo = extractUserInfoFromToken(token);
        
        // Create or update user profile with available information
        const updatedProfile: UserProfile = {
          ...userProfile,
          username: tokenUserInfo.username || localUserInfo.username || 'کاربر',
          mobileNumber: localUserInfo.mobileNumber || tokenUserInfo.mobileNumber || 'نامشخص',
          email: tokenUserInfo.email || userProfile?.email,
          isActive: true
        };
        
        setUserProfile(updatedProfile);
        
        console.log('🔍 User profile created from token and localStorage:', updatedProfile);
      } else {
        // No token available
        setUserProfile({
          mobileNumber: 'کاربر وارد نشده',
          isActive: false
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      notification.error({ 
        message: 'خطا در دریافت اطلاعات پروفایل',
        description: 'لطفا دوباره تلاش کنید'
      });
      
      // Fallback to basic profile from localStorage
      const localUserInfo = httpService.getUserInfo();
      if (localUserInfo.mobileNumber) {
        setUserProfile({
          username: localUserInfo.username || 'کاربر',
          mobileNumber: localUserInfo.mobileNumber,
          isActive: true
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const clearBrowserCache = async () => {
    try {
      setIsClearingCache(true);
      
      // Use the utility function to clear cache
      const result = await clearShahsaaCache({
        clearLocalStorage: true,
        clearSessionStorage: true,
        clearCookies: false,
        clearIndexedDB: false,
        preserveAuthTokens: true
      });

      if (result.success) {
        const clearedCount = result.clearedItems.length;
        notification.success({ 
          message: 'کش مرورگر پاک شد',
          description: `${clearedCount} مورد از کش مربوط به سایت app.shahsaa.com با موفقیت پاک شد`
        });
        
        // Log detailed information for debugging
        console.log('Cache cleared successfully:', result.clearedItems);
        if (result.errors.length > 0) {
          console.warn('Some cache clearing operations had errors:', result.errors);
        }
      } else {
        throw new Error('Cache clearing failed');
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      notification.error({ 
        message: 'خطا در پاک کردن کش',
        description: 'لطفا دوباره تلاش کنید یا کش مرورگر را به صورت دستی پاک کنید'
      });
    } finally {
      setIsClearingCache(false);
    }
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      // Clear user data
      httpService.logout();
      notification.success({ 
        message: 'خروج موفقیت‌آمیز بود',
        description: 'شما با موفقیت از حساب کاربری خارج شدید'
      });
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
      notification.error({ 
        message: 'خطا در خروج',
        description: 'لطفا دوباره تلاش کنید'
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'نامشخص';
    try {
      return new Date(dateString).toLocaleDateString('fa-IR');
    } catch {
      return 'نامشخص';
    }
  };

  const getDisplayName = () => {
    // First try to get username from current profile
    if (userProfile?.username && userProfile.username !== 'کاربر') {
      return userProfile.username;
    }
    
    // Fallback to localStorage username
    const localUsername = httpService.getUsername();
    if (localUsername) {
      return localUsername;
    }
    
    // Fallback to first name + last name
    if (userProfile?.firstName && userProfile?.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName}`;
    }
    
    return 'کاربر';
  };

  if (isLoading) {
    return (
      <div className="profile-container">
        <div className="profile-content">
          <Card className="card-themed">
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text>در حال بارگذاری اطلاعات پروفایل...</Text>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-content">
        <Card className="card-themed">
          {/* Header Section */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <Avatar 
              size={80} 
              icon={<UserOutlined />} 
              style={{ 
                marginBottom: 16,
                backgroundColor: theme.primary,
                fontSize: '32px'
              }} 
            />
            <Title level={2} className="text-primary" style={{ marginBottom: 8 }}>
              {getDisplayName()}
            </Title>
            <Tag 
              color={userProfile?.isActive ? theme.primary : theme.primary}
              style={{ fontSize: '14px', padding: '4px 12px' }}
            >
              {userProfile?.isActive ? 'فعال' : 'غیرفعال'}
            </Tag>
          </div>

          <Divider />

          {/* User Information Section */}
          <div style={{ marginBottom: 32 }}>
            <Title level={4} className="text-primary" style={{ marginBottom: 16 }}>
              اطلاعات شخصی
            </Title>
            
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div className="info-item">
                  <PhoneOutlined className="info-icon" />
                  <div>
                    <Text strong className="text-primary">شماره موبایل</Text>
                    <div>
                      <Text type="secondary" className="text-secondary">
                        {userProfile?.mobileNumber || httpService.getMobileNumber() || 'نامشخص'}
                      </Text>
                    </div>
                  </div>
                </div>
              </Col>
              
              {userProfile?.email && (
                <Col xs={24} sm={12}>
                  <div className="info-item">
                    <MailOutlined className="info-icon" />
                    <div>
                      <Text strong className="text-primary">ایمیل</Text>
                      <div>
                        <Text type="secondary" className="text-secondary">{userProfile.email}</Text>
                      </div>
                    </div>
                  </div>
                </Col>
              )}
              
              {userProfile?.joinDate && (
                <Col xs={24} sm={12}>
                  <div className="info-item">
                    <CalendarOutlined className="info-icon" />
                    <div>
                      <Text strong className="text-primary">تاریخ عضویت</Text>
                      <div>
                        <Text type="secondary" className="text-secondary">{formatDate(userProfile.joinDate)}</Text>
                      </div>
                    </div>
                  </div>
                </Col>
              )}
              
              {userProfile?.lastLogin && (
                <Col xs={24} sm={12}>
                  <div className="info-item">
                    <ClockCircleOutlined className="info-icon" />
                    <div>
                      <Text strong className="text-primary">آخرین ورود</Text>
                      <div>
                        <Text type="secondary" className="text-secondary">{formatDate(userProfile.lastLogin)}</Text>
                      </div>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </div>

          <Divider />

          {/* Action Buttons Section */}
          <div className="profile-actions">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Button
                type="primary"
                size="large"
                block
                icon={<EditOutlined />}
                onClick={() => notification.info({ message: 'به زودی...', description: 'قابلیت ویرایش پروفایل به زودی اضافه خواهد شد' })}
                className="btn-primary"
              >
                ویرایش پروفایل
              </Button>
              
              <Button
                type="default"
                size="large"
                block
                icon={<BulbOutlined />}
                onClick={() => {
                  toggleTheme();
                  notification.success({ 
                    message: 'تم تغییر کرد',
                    description: `تم به ${mode === 'dark' ? 'روشن' : 'تاریک'} تغییر یافت`
                  });
                }}
                style={{
                  borderColor: theme.secondary,
                  color: theme.secondary,
                  backgroundColor: 'transparent'
                }}
              >
                تغییر تم ({mode === 'dark' ? 'تاریک' : 'روشن'})
              </Button>
              
              <Tooltip title="پاک کردن کش مرورگر برای سایت app.shahsaa.com - این کار فقط کش مربوط به این سایت را پاک می‌کند">
                <Button
                  type="default"
                  size="large"
                  block
                  loading={isClearingCache}
                  onClick={clearBrowserCache}
                  icon={<ClearOutlined />}
                  style={{ 
                    borderColor: '#FFA500',
                    color: '#FFA500'
                  }}
                >
                  {isClearingCache ? 'در حال پاک کردن کش...' : 'پاک کردن کش مرورگر'}
                </Button>
              </Tooltip>
              
              <Button
                type="primary"
                danger
                size="large"
                block
                loading={isLoggingOut}
                onClick={handleLogout}
                icon={<LogoutOutlined />}
                className="btn-primary"
              >
                {isLoggingOut ? 'در حال خروج...' : 'خروج از حساب کاربری'}
              </Button>
            </Space>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Profile; 